/**
 * Centralized route path definitions
 * Single source of truth for all application routes
 */

export const ROUTES = {
  // Root
  ROOT: "/",

  // Authentication
  AUTH: {
    LOGIN: "/auth/login",
    HANDOFF: "/auth/handoff",
    // The sign-in page for the software provider: the badges of the mesh's
    // hidden login sources. Linked from nowhere.
    ACCUR8_LOGIN: "/page/accur8-login",
  },

  // Data pages: composed from the model by a page module (src/page-dsl/pages,
  // customers/<id>/pages) and served by the one generic route. /page/<name> is
  // a data page; /app/... is an application. The hand-wired /page/... routes
  // and the interim /page/... URLs were retired together
  // (FEATURE-20260917-retire-hand-wired-pages-replaced-by-page-dsl).
  PAGE: {
    ROUTE: "/page/:name",
    FOR: (name: string) => `/page/${name}`,
    // A page over one record: /page/monitor/<uid> — the id reaches the page as sdk.params.id.
    ITEM_ROUTE: "/page/:name/:id",
    ITEM_FOR: (name: string, id: string) => `/page/${name}/${encodeURIComponent(id)}`,
    // The last hand-wired page under /page: the Jobs view over
    // job.ahsdevopsvisible. The customer profile that serves this page carries a
    // DSL counterpart of its own, and this route goes once that page has been
    // judged against real rows
    // (FEATURE-20260917-retire-hand-wired-pages-replaced-by-page-dsl, phase 2).
    AHS_SERVICES: "/page/ahs-services",
  },

  // Operational Tools
  APP: {
    DASHBOARD: "/app/dashboard",
    LOGS: "/app/logs",
    // channel (optional): OMIT IT unless the caller genuinely knows which channel it
    // wants. The viewer resolves an absent channel from the run's own registered
    // channels (one -> use it; several -> ask). Defaulting to "console" here is what
    // sent every applog-only run to an empty "this process produced no logs" view.
    // parser (optional): the job's default log parser, appended as &parser= so the
    // viewer pre-selects it. Omitted when unset — the viewer then derives one from
    // the channel (applog -> applog) and finally falls back to "structured".
    LOGS_WITH_PARAMS: (processUid: string, channel?: string, parser?: string) =>
      `/app/logs?processUid=${processUid}` +
      (channel ? `&channel=${encodeURIComponent(channel)}` : "") +
      (parser ? `&parser=${encodeURIComponent(parser)}` : ""),
    LOGS_TAIL: (processUid: string, channel?: string, parser?: string) =>
      `/app/logs?processUid=${processUid}` +
      (channel ? `&channel=${encodeURIComponent(channel)}` : "") +
      `&start=${encodeURIComponent("-20b|-10m")}&end=tail` +
      (parser ? `&parser=${encodeURIComponent(parser)}` : ""),
    SQL_QUERY: "/app/sql-query",
    SQL_EXPLAIN: "/app/sql-explain",
    PROMPT: "/app/prompt",
    DEVTOOLS: "/app/devtools",
    DEVTOOLS_WITH_KEY: (readerKey: string) => `/app/devtools/${readerKey}`,
    // The RPC traffic view for a PROCESS RUN. Prefer this over DEVTOOLS_WITH_KEY:
    // a mailbox key is a credential, and addressing by run means the browser never
    // holds one — the server authorizes the run and resolves the mailbox itself
    // (/api/processrun_tail).
    DEVTOOLS_FOR_PROCESS: (processUid: string) =>
      `/app/devtools?processUid=${encodeURIComponent(processUid)}`,
    WHOAMI: "/app/whoami",
    PROFILE: "/app/profile",
    RPC_TEST: "/app/rpc-test",
    REGISTRY: "/app/registry",
    PINGER: "/app/pinger",
  },

  // Admin pages (ContinuumDevops-gated)
  ADMIN: {
    DIRECTORY: "/app/admin/directory",
    PENDING: "/app/admin/pending",
    // UNCORRELATED is the previous URL for PENDING. Kept for one
    // release as a redirect target so existing bookmarks still land.
    UNCORRELATED: "/app/admin/uncorrelated",
  },

  // Loom 2.0 (chat over the CFS)
  LOOM: {
    CHAT: "/app/loom",
    // One conversation: /app/loom/c/<conversationUid>?at=<messageUid>, so a chat (and the
    // spot in it) is a shareable link. FEATURE-20261030-loom-chat-url-names-the-conversation
    CONVERSATION: "/app/loom/c",
    BROWSER: "/app/loom/files",
  },

  // Fileset: the EDITOR is an application (Monaco, revisions, access tabs).
  // The list has a DSL counterpart at /page/filesets; this one stays until
  // glen has judged that page's row visibility for a non-admin and ruled on
  // the My / All filter
  // (FEATURE-20260917-retire-hand-wired-pages-replaced-by-page-dsl, phase 2).
  FILESET: {
    LIST: "/app/fileset",
    EDITOR_ROUTE: "/app/fileset/:filesetId",
    EDITOR: (filesetId: string) => `/app/fileset/${filesetId}`,
  },

  // Checkpoint programs run by the Go runtime: the saved (paused) ones, and one read as a program
  // (FEATURE-20261002-go-runtime-for-checkpoint).
  CHECKPOINT: {
    SAVED: "/app/checkpoint/saved",
    SAVED_PROGRAM_ROUTE: "/app/checkpoint/saved/:checkpointUid",
    SAVED_PROGRAM: (checkpointUid: string) => `/app/checkpoint/saved/${encodeURIComponent(checkpointUid)}`,
  },

  // One recorded run of a program function (its checkpoint process), read with everything around it: the ask,
  // who ran it, the function and its doc, input and result, the justification, what the program
  // touches, its source and the job and monitor it acted on. The list is a data page, at
  // /page/program-runs.
  PROGRAM_RUN: {
    DETAIL_ROUTE: "/app/program-run/:runUid",
    DETAIL: (runUid: string) => `/app/program-run/${encodeURIComponent(runUid)}`,
  },

  // Monitor detail: a record card with the Thresholds table and the kind
  // plugin's health panel, so it stays hand-wired until the record DSL lands
  // (FEATURE-20260831-more-dsls-form-and-record-page). The list is a data
  // page, at /page/monitors.
  MONITOR: {
    DETAIL_ROUTE: "/app/monitor/:monitorUid",
    DETAIL: (monitorUid: string) => `/app/monitor/${monitorUid}`,
  },

  // Demo Pages
  DEMO: {
    PLAYGROUND: "/demo/playground",
    PARENT_CHILD: "/demo/parent-child",
    TABLE_CHANGE_TRACKING: "/demo/table-change-tracking",
    FORM: "/demo/form",
  },
} as const;

/**
 * Default redirect after successful login. Callers use postLoginRedirect()
 * from src/customer, which lets the customer profile's `home` override this.
 */
export const DEFAULT_POST_LOGIN_REDIRECT = ROUTES.PAGE.FOR("jobs");
